import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Search, Plus, Pencil, Trash2, Building } from 'lucide-react'
import { Department } from '@/models/department.model'
import { departmentsService, DepartmentMutationPayload } from '@/services/departmentsService'
import { facultiesService } from '@/services/facultiesService'
import { DepartmentFormDialog, DepartmentFormValues } from '@/components/app/departments/DepartmentFormDialog'
import { ConfirmDeleteDialog } from '@/components/app/common/ConfirmDeleteDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/utils/cn'
import { PaginationMeta } from '@/models/templates/pagination.model'

interface FeedbackBanner {
  type: 'success' | 'error'
  message: string
}

export const DepartmentsPage: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([])
  const [faculties, setFaculties] = useState<Array<{ id: string; name: string }>>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [pagination, setPagination] = useState<{ page: number; perPage: number }>({ page: 1, perPage: 12 })
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [banner, setBanner] = useState<FeedbackBanner | null>(null)
  const [formOpen, setFormOpen] = useState<boolean>(false)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false)
  const [departmentToDelete, setDepartmentToDelete] = useState<Department | null>(null)

  const totalPages = meta ? Math.max(1, Math.ceil(meta.total / meta.perPage)) : 1
  const disableActions = faculties.length === 0

  const showBanner = (type: 'success' | 'error', message: string) => {
    setBanner({ type, message })
  }

  const loadDepartments = useCallback(
    async (page: number = pagination.page, perPage: number = pagination.perPage) => {
      setIsLoading(true)
      try {
        const response = await departmentsService.getDepartmentsPage({ page, perPage })
        setDepartments(response.data)
        setMeta(response.meta)
      } catch (error) {
        showBanner('error', (error as Error).message || 'Failed to load departments.')
      } finally {
        setIsLoading(false)
      }
    },
    [pagination.page, pagination.perPage],
  )

  useEffect(() => {
    const loadFaculties = async () => {
      try {
        const response = await facultiesService.getFacultiesPage({ page: 1, perPage: 100 })
        setFaculties(response.data.map((faculty) => ({ id: faculty.id, name: faculty.name })))
      } catch (error) {
        showBanner('error', (error as Error).message || 'Failed to load faculties.')
      }
    }

    loadDepartments(pagination.page, pagination.perPage)
    loadFaculties()
  }, [loadDepartments, pagination.page, pagination.perPage])

  useEffect(() => {
    if (!banner) return
    const timeout = setTimeout(() => setBanner(null), 4000)
    return () => clearTimeout(timeout)
  }, [banner])

  const facultyLookup = useMemo(() => {
    return faculties.reduce<Record<string, string>>((acc, faculty) => {
      acc[faculty.id] = faculty.name
      return acc
    }, {})
  }, [faculties])

  const filteredDepartments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) {
      return departments
    }

    return departments.filter((department) =>
      department.name.toLowerCase().includes(term) || (department.description ?? '').toLowerCase().includes(term),
    )
  }, [departments, searchTerm])

  const openCreateModal = () => {
    setFormMode('create')
    setSelectedDepartment(null)
    setFormError(null)
    setFormOpen(true)
  }

  const openEditModal = (department: Department) => {
    setFormMode('edit')
    setSelectedDepartment(department)
    setFormError(null)
    setFormOpen(true)
  }

  const openDeleteDialog = (department: Department) => {
    setDepartmentToDelete(department)
    setDeleteOpen(true)
  }

  const handleFormSubmit = async (values: DepartmentFormValues) => {
    const payload: DepartmentMutationPayload = {
      facultyId: values.facultyId,
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      isActive: values.isActive,
    }

    try {
      setSubmitting(true)
      setFormError(null)
      if (formMode === 'create') {
        if (disableActions) {
          const message = 'Create a faculty before adding departments.'
          setFormError(message)
          showBanner('error', message)
          return
        }
        await departmentsService.createDepartment(payload)
        showBanner('success', 'Department created successfully.')
        setPagination((prev) => ({ ...prev, page: 1 }))
        await loadDepartments(1, pagination.perPage)
      } else if (selectedDepartment) {
        await departmentsService.updateDepartment(selectedDepartment.id, payload)
        showBanner('success', 'Department updated successfully.')
        await loadDepartments()
      }
      setFormOpen(false)
      setFormError(null)
    } catch (error) {
      const message = (error as Error).message || 'Unable to save department.'
      setFormError(message)
      showBanner('error', message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteDepartment = async () => {
    if (!departmentToDelete) return

    try {
      setSubmitting(true)
      await departmentsService.deleteDepartment(departmentToDelete.id)
      showBanner('success', 'Department deleted successfully.')
      setDeleteOpen(false)
      await loadDepartments()
    } catch (error) {
      showBanner('error', (error as Error).message || 'Unable to delete department.')
    } finally {
      setSubmitting(false)
    }
  }

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return
    setPagination((prev) => ({ ...prev, page: newPage }))
  }

  const handlePerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(event.target.value)
    setPagination({ page: 1, perPage: value })
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 flex items-center gap-2">
            Departments
            <Building className="h-6 w-6 text-[#7b2a40]" />
          </h1>
          <p className="text-secondary-600 mt-1">Maintain the departments that belong to each faculty.</p>
        </div>
        <Button onClick={openCreateModal} className="self-start" disabled={disableActions}>
          <Plus className="mr-2 h-4 w-4" />
          Create Department
        </Button>
      </header>

      {disableActions && (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          Create a faculty before adding departments.
        </div>
      )}

      {banner && (
        <div
          className={cn(
            'rounded-md border px-4 py-3 text-sm',
            banner.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700',
          )}
        >
          {banner.message}
        </div>
      )}

      <section className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400 z-10" />
          <Input
            placeholder="Search departments…"
            className="pl-9"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value)
              setPagination((prev) => ({ ...prev, page: 1 }))
            }}
            aria-label="Search departments"
          />
        </div>
        <div className="flex items-center gap-3">
          <select
            value={pagination.perPage}
            onChange={handlePerPageChange}
            className="rounded-md border border-secondary-300 px-3 py-2 text-sm text-secondary-700 focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40"
            aria-label="Items per page"
          >
            {[6, 12, 24, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>
      </section>

      {isLoading ? (
        <div className="flex justify-center py-20 text-secondary-500">Loading departments…</div>
      ) : filteredDepartments.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-secondary-600">
            No departments found. {searchTerm ? 'Try a different search term.' : 'Create your first department to get started.'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredDepartments.map((department) => (
            <Card key={department.id} className="flex flex-col border border-secondary-200">
              <CardHeader className="pb-4 rounded-t-md bg-primary bg-gradient-to-r from-[#7b2a40]/90 to-[#c4788a]/70 min-h-[120px]">
                <CardTitle className="text-lg text-white">{department.name}</CardTitle>
                <CardDescription className="text-white/60">
                  {facultyLookup[department.facultyId] ?? 'Unknown faculty'}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                <p className="text-sm text-secondary-600 line-clamp-3">
                  {department.description || 'No description provided.'}
                </p>
                <Badge className={cn(department.isActive ? 'bg-green-100 text-green-800' : 'bg-secondary-100 text-secondary-700')}>
                  {department.isActive ? 'Active' : 'Inactive'}
                </Badge>
                <p className="text-xs text-secondary-400">
                  Updated {department.updatedAt ? new Date(department.updatedAt).toLocaleDateString() : 'Recently'}
                </p>
                <div className="flex gap-2">
                  <Button type="button" size="sm" className="flex-1" onClick={() => openEditModal(department)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => openDeleteDialog(department)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <DepartmentFormDialog
        open={formOpen}
        mode={formMode}
        faculties={faculties}
        initialValues={
          selectedDepartment
            ? {
                facultyId: selectedDepartment.facultyId,
                name: selectedDepartment.name,
                description: selectedDepartment.description ?? '',
                isActive: selectedDepartment.isActive,
              }
            : undefined
        }
        submitting={submitting}
        errorMessage={formError}
        onSubmit={handleFormSubmit}
        onClose={() => {
          setFormOpen(false)
          setFormError(null)
        }}
      />

      <ConfirmDeleteDialog
        open={deleteOpen}
        entityLabel="Department"
        entityName={departmentToDelete?.name}
        onConfirm={handleDeleteDepartment}
        onClose={() => setDeleteOpen(false)}
        loading={submitting}
      />

      <PaginationControls
        page={pagination.page}
        totalPages={totalPages}
        meta={meta}
        onPageChange={handlePageChange}
      />
    </div>
  )
}

interface PaginationControlsProps {
  page: number
  totalPages: number
  meta: PaginationMeta | null
  onPageChange: (page: number) => void
}

const PaginationControls: React.FC<PaginationControlsProps> = ({ page, totalPages, meta, onPageChange }) => {
  if (!meta) return null

  return (
    <div className="flex flex-col items-center justify-between gap-3 border-t border-secondary-200 pt-4 text-sm text-secondary-600 md:flex-row">
      <div>
        Showing page {page} of {totalPages} • Total {meta.total} departments
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
          Previous
        </Button>
        <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
          Next
        </Button>
      </div>
    </div>
  )
}

export default DepartmentsPage
